/**
 * Client clock estimation and drift-correction decisions. Pure logic; the web app feeds it
 * samples from `time.ping` round trips and player readings. These are tunable product defaults,
 * not precision guarantees.
 */

export interface ClockSample {
  /** Local monotonic-ish clock (performance.timeOrigin + performance.now()) when the ping left. */
  t0: number;
  /** Local clock when the pong arrived. */
  t1: number;
  serverNowMs: number;
}

export class ClockSync {
  private samples: { offset: number; rtt: number }[] = [];
  constructor(
    private readonly maxSamples = 8,
    private readonly bestOf = 3,
  ) {}

  add(sample: ClockSample): void {
    const rtt = sample.t1 - sample.t0;
    if (!(rtt >= 0) || rtt > 10_000) return; // discard nonsense and pathological samples
    const offset = sample.serverNowMs - (sample.t0 + rtt / 2);
    this.samples.push({ offset, rtt });
    if (this.samples.length > this.maxSamples) this.samples.shift();
  }

  reset(): void {
    this.samples = [];
  }

  get sampleCount(): number {
    return this.samples.length;
  }

  /** Median offset of the lowest-RTT samples; null until at least one sample. */
  get offsetMs(): number | null {
    if (this.samples.length === 0) return null;
    const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, this.bestOf);
    const offsets = best.map((s) => s.offset).sort((a, b) => a - b);
    const mid = Math.floor(offsets.length / 2);
    return offsets.length % 2 ? offsets[mid]! : (offsets[mid - 1]! + offsets[mid]!) / 2;
  }

  /** Best RTT seen, to show a confidence indicator. */
  get bestRttMs(): number | null {
    if (this.samples.length === 0) return null;
    return Math.min(...this.samples.map((s) => s.rtt));
  }

  serverNow(localNow: number): number | null {
    const o = this.offsetMs;
    return o === null ? null : localNow + o;
  }
}

export interface PlaybackTiming {
  status: 'idle' | 'scheduled' | 'playing';
  startsAtMs: number | null;
  durationMs: number | null;
}

/** Desired media position in ms: clamp(serverNow - startsAt, 0, duration). Null when idle. */
export function desiredPositionMs(t: PlaybackTiming, serverNowMs: number): number | null {
  if (t.status === 'idle' || t.startsAtMs === null || t.durationMs === null) return null;
  return Math.min(Math.max(serverNowMs - t.startsAtMs, 0), t.durationMs);
}

/** Milliseconds until the scheduled start (0 if already started). */
export function msUntilStart(t: PlaybackTiming, serverNowMs: number): number {
  if (t.startsAtMs === null) return 0;
  return Math.max(0, t.startsAtMs - serverNowMs);
}

export type PlayerState = 'unstarted' | 'playing' | 'paused' | 'buffering' | 'ended' | 'cued';

export interface DriftReading {
  nowMs: number;
  following: boolean;
  visible: boolean;
  blocked: boolean;
  errored: boolean;
  playerState: PlayerState;
  actualMs: number;
  desiredMs: number | null;
}

export type DriftDecision =
  | {
      action: 'none';
      reason:
        | 'not_following'
        | 'hidden'
        | 'blocked'
        | 'errored'
        | 'not_playing'
        | 'idle'
        | 'in_tolerance'
        | 'confirming'
        | 'cooldown';
    }
  | { action: 'seek'; toMs: number; driftMs: number };

export interface DriftConfig {
  toleranceMs: number;
  consecutiveRequired: number;
  cooldownMs: number;
}

export const DEFAULT_DRIFT: DriftConfig = {
  toleranceMs: 1_500,
  consecutiveRequired: 2,
  cooldownMs: 15_000,
};

/**
 * Decides whether to seek. Never corrects while paused/buffering/hidden/blocked/unsynced, only
 * after repeated excessive drift, and at most once per cooldown. Never changes playback rate.
 */
export class DriftController {
  private strikes = 0;
  private lastCorrectionAt = -Infinity;
  constructor(private readonly cfg: DriftConfig = DEFAULT_DRIFT) {}

  reset(): void {
    this.strikes = 0;
  }

  evaluate(r: DriftReading): DriftDecision {
    const gate = this.gate(r);
    if (gate) {
      this.strikes = 0;
      return { action: 'none', reason: gate };
    }
    const drift = r.actualMs - r.desiredMs!;
    if (Math.abs(drift) <= this.cfg.toleranceMs) {
      this.strikes = 0;
      return { action: 'none', reason: 'in_tolerance' };
    }
    this.strikes++;
    if (this.strikes < this.cfg.consecutiveRequired)
      return { action: 'none', reason: 'confirming' };
    if (r.nowMs - this.lastCorrectionAt < this.cfg.cooldownMs)
      return { action: 'none', reason: 'cooldown' };
    this.strikes = 0;
    this.lastCorrectionAt = r.nowMs;
    return { action: 'seek', toMs: r.desiredMs!, driftMs: drift };
  }

  private gate(r: DriftReading): Exclude<DriftDecision, { action: 'seek' }>['reason'] | null {
    if (r.desiredMs === null) return 'idle';
    if (!r.following) return 'not_following';
    if (!r.visible) return 'hidden';
    if (r.blocked) return 'blocked';
    if (r.errored) return 'errored';
    if (r.playerState !== 'playing') return 'not_playing';
    return null;
  }
}
